/**
 * Join lifecycle for Stream Video.
 *
 * The SDK constructor fires `connectUser` without awaiting it. Calling `call.join()`
 * in the same tick can hit `getToken()` before the token is set. Effect cleanup that
 * `disconnectUser()`s mid-join produces:
 * "User token is not set. Either client.connectUser wasn't called or client.disconnect was called"
 */

export const STREAM_TOKEN_NOT_SET =
  "User token is not set. Either client.connectUser wasn't called or client.disconnect was called"

export type StreamJoinSteps = {
  connectUser: () => Promise<void>
  join: () => Promise<void>
  leave: () => Promise<void>
  isCancelled: () => boolean
}

export type StreamJoinResult = 'joined' | 'cancelled'

/**
 * Connect, then join. If the caller unmounted (or React Strict Mode re-ran the
 * effect) mid-flight, swallow the error and leave instead of surfacing it.
 */
export const connectAndJoinCall = async (steps: StreamJoinSteps): Promise<StreamJoinResult> => {
  await steps.connectUser()
  if (steps.isCancelled()) {
    return 'cancelled'
  }
  try {
    await steps.join()
  } catch (err) {
    if (steps.isCancelled()) {
      return 'cancelled'
    }
    throw err
  }
  if (steps.isCancelled()) {
    await steps.leave().catch(() => null)
    return 'cancelled'
  }
  return 'joined'
}

export const streamJoinErrorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : 'Failed to join Stream call'
